import { isTouchDevice } from '../input/touch-controls';
import { t } from '../i18n/i18n';

/**
 * game-concept.md §13a — shown while the run-start camera fly-through plays.
 * Purely a hint + skip affordance; the camera/input gating itself lives in
 * dungeon-instance-bootstrap.ts. Skippable by clicking this button, pressing
 * Space (dungeon-instance-bootstrap.ts repurposes the jump key), or clicking
 * the canvas.
 */
export class FlyThroughOverlay {
  private readonly root: HTMLDivElement;
  private readonly skipButton: HTMLButtonElement;
  onSkip: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.dataset.hud = 'flythrough';
    this.root.className = [
      'fixed', 'inset-0', 'z-[1200]', 'flex', 'flex-col', 'items-end', 'justify-end',
      // `pb-28`, not `p-6`: the carried-loot plaque owns the bottom-right
      // corner for the whole run, and the skip button was landing on top of
      // it — the one moment both are on screen is exactly the fly-through.
      //
      // On a phone the bottom-right corner belongs to something else again,
      // and `pb-28` is nowhere near enough — app.css raises it off the thumbs
      // under `.touch-play`. See the note there.
      'gap-2', 'p-6', 'pb-28', 'pointer-events-none', 'select-none', 'hidden',
    ].join(' ');

    const hint = document.createElement('div');
    hint.className = 'frame-pewter px-3 py-1.5 text-text-dim text-[clamp(0.8rem,1.8vw,0.95rem)]';
    hint.textContent = t('Previewing the route to the exit...');

    this.skipButton = document.createElement('button');
    this.skipButton.type = 'button';
    // A phone has no space bar, and naming a key nobody has is worse than
    // naming none: it reads as an instruction the player cannot follow.
    this.skipButton.textContent = isTouchDevice() ? t('Skip') : t('Skip ({key})', { key: 'Space' });
    this.skipButton.className = ['pointer-events-auto', 'btn', 'btn-stone', 'px-4'].join(' ');
    this.skipButton.addEventListener('click', () => this.onSkip?.());

    this.root.append(hint, this.skipButton);
    container.appendChild(this.root);
  }

  show(): void {
    this.root.classList.remove('hidden');
  }

  hide(): void {
    this.root.classList.add('hidden');
  }

  dispose(): void {
    this.root.remove();
  }
}
